import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { MegaMenu } from "./MegaMenu";
import { SearchBar } from "./SearchBar";

export function Header() {
  const { t, prefix, lang } = useI18n();
  const isSv = lang === "sv";
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <>
      {/* Promo strip */}
      <div className="hidden bg-ink-950 py-1.5 text-center text-[11px] font-medium tracking-wide text-white/80 md:block">
        <span className="container-page flex items-center justify-center gap-3">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            {isSv
              ? "18+ · Spela ansvarsfullt · Spelpaus.se"
              : "18+ · Gamble responsibly · Spelpaus.se"}
          </span>
          <span className="text-white/30">·</span>
          <span className="text-white/70">
            {isSv
              ? "Stödlinjen 020-81 91 00"
              : "Help line: Stödlinjen 020-81 91 00"}
          </span>
        </span>
      </div>

      <header
        className={`sticky top-0 z-30 transition-all ${
          scrolled
            ? "border-b border-ink-100 bg-white/85 shadow-card backdrop-blur-md"
            : "border-b border-transparent bg-white/0"
        }`}
      >
        <div className="container-page flex h-16 items-center justify-between gap-3">
          <Link to={prefix} className="no-underline">
            <Logo />
          </Link>

          <MegaMenu orientation="desktop" />

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label={t("nav.search")}
              aria-expanded={searchOpen}
              className="grid h-10 w-10 place-items-center rounded-lg text-ink-700 transition hover:bg-ink-100"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
            </button>
            <LanguageSwitcher />
            <Link
              to={`${prefix}/casino`}
              className="hidden btn-primary text-sm sm:inline-flex"
            >
              {t("cta.compareNow")}
              <span aria-hidden>→</span>
            </Link>
            <button
              type="button"
              className="grid h-10 w-10 place-items-center rounded-lg text-ink-700 hover:bg-ink-100 lg:hidden"
              aria-label={t("nav.menu")}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 6l12 12M6 18L18 6" />
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 7h16M4 13h16M4 19h12" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-ink-100 bg-white py-3">
            <div className="container-page">
              <SearchBar onSelect={() => setSearchOpen(false)} autoFocus />
            </div>
          </div>
        )}
      </header>

      {/* Mobile drawer */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-ink-950/40 backdrop-blur-sm lg:hidden"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <aside
            className="fixed inset-y-0 right-0 z-50 w-[min(360px,90vw)] overflow-y-auto bg-ink-50 p-4 shadow-card-hover lg:hidden"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between">
              <Logo />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("nav.close")}
                className="grid h-10 w-10 place-items-center rounded-lg text-ink-700 hover:bg-ink-100"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 6l12 12M6 18L18 6" />
                </svg>
              </button>
            </div>
            <div className="mt-4">
              <SearchBar onSelect={() => setOpen(false)} />
            </div>
            <div className="mt-4">
              <MegaMenu orientation="mobile" />
            </div>
            <Link
              to={`${prefix}/casino`}
              className="mt-4 btn-primary w-full"
            >
              {t("cta.compareNow")}
            </Link>
          </aside>
        </>
      )}
    </>
  );
}
